"use client";

import React, { useState, useEffect, useRef } from "react";
import { CheckCircle, XCircle, SkipForward, Loader2 } from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";

interface VisaModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: any;
  code: string;
  type: string;
  onAccept: () => void | Promise<void>;
  onDecline: (reason: string) => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
}

interface visaDetailsType {
  address1: string;
  code: string;
  email: string;
  full_name: string;
  id: number;
  name: string;
  reg_first_name?: string;
  reg_family_name?: string;
  reg_name_badge?: string;
  reg_affiliation?: string;
  reg_type?: string;
  title?: string;
  surname?: string;
  given_name?: string;
  nationality?: string;
  place_of_birth?: string;
  passport_country: string;
  passport_number: string;
  passport_issue_date?: string;
  passport_expiry_date?: string;
  registration_id: string;
  rejection_reason: string;
  birth_date?: string;
  gender?: string;
  status: string;
  type: string;
}

const DECLINE_REASONS = [
  "Document is not legible",
  "Document is expired",
  "Document is not official",
  "Information doesn't match registration",
  "Document appears to be altered",
  "Other",
];

const Field: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <span className="text-sm text-gray-900 break-words">
      {value && String(value).trim() !== "" ? value : <em className="text-gray-400">—</em>}
    </span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-4">
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-blue-700 border-b border-gray-200 pb-1">
      {title}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
      {children}
    </div>
  </div>
);

const nameMatches = (visa: visaDetailsType | null): boolean => {
  if (!visa) return true;
  const norm = (s?: string | null) =>
    (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
  const reg = norm(`${visa.reg_first_name ?? ""} ${visa.reg_family_name ?? ""}`);
  const visaName = norm(`${visa.given_name ?? ""} ${visa.surname ?? ""}`);
  if (!reg || !visaName) return true;
  return reg === visaName;
};

export default function VisaModal({
  isOpen,
  onClose,
  row,
  code,
  type,
  onAccept,
  onDecline,
  onSkip,
}: VisaModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [textReason, setTextReason] = useState<string>("");
  const [isDeclineActive, setIsDeclineActive] = useState<boolean>(false);
  const [visaDetails, setVisaDetails] = useState<visaDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") handleClose();
    }
    document.addEventListener("keydown", handleEscapeKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Re-fetch details whenever the visa case changes (open or advance to next)
  useEffect(() => {
    if (!isOpen || !code || !type) return;
    setVisaDetails(null);
    setIsDeclineActive(false);
    setSelectedReason("");
    setTextReason("");
    let cancelled = false;
    axios
      .get(`${base_url}/api/admin/get_visa_details/${code}/${type}`)
      .then((response) => {
        if (!cancelled) setVisaDetails(response.data);
      })
      .catch((error) => console.error("Error fetching visa details:", error));
    return () => {
      cancelled = true;
    };
  }, [isOpen, code, type]);

  const resetForm = () => {
    setSelectedReason("");
    setTextReason("");
    setIsDeclineActive(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/visa/accept`, {
        code: row?.code ?? code,
        type: row?.type ?? type,
      });
      resetForm();
      await Promise.resolve(onAccept());
    } catch (error) {
      console.error("Error accepting visa:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclineActive(false);
    try {
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/decline_visa_letter`, {
        reason: textReason,
        code: row?.code ?? code,
        type: row?.type ?? type,
      });
      resetForm();
      await Promise.resolve(onDecline(textReason));
    } catch (error) {
      console.error("Error declining visa letter:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) return;
    setIsLoading(true);
    try {
      resetForm();
      await Promise.resolve(onSkip());
    } finally {
      setIsLoading(false);
    }
  };

  const handleReasonSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedReason(value);
    setTextReason(value === "Other" ? "" : value);
  };

  if (!isOpen) return null;

  const v = visaDetails;
  const matches = nameMatches(v);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl flex flex-col p-6 relative max-h-[92vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <XCircle className="h-5 w-5" />
        </button>

        <div className="mb-4 pr-8">
          <h2 id="modal-title" className="text-xl font-bold">
            Visa letter review
          </h2>
          <p className="text-sm text-gray-500">
            Code <span className="font-mono">{v?.code ?? code}</span> &middot; Type{" "}
            <span className="font-mono">{v?.type ?? type}</span>
          </p>
        </div>

        {!v ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : (
          <>
            <Section title="Registration">
              <Field
                label="Registered name"
                value={`${v.reg_first_name ?? ""} ${v.reg_family_name ?? ""}`.trim()}
              />
              <Field label="Email" value={v.email} />
              <Field label="Affiliation" value={v.reg_affiliation} />
              <Field label="Type" value={v.reg_type} />
            </Section>

            {!matches && (
              <div className="mb-4 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                The name in the passport does not match the registered name.
                Please review carefully before approving.
              </div>
            )}

            <Section title="Passport — applicant details">
              <Field label="Title" value={v.title} />
              <div className="hidden md:block" aria-hidden="true" />
              <Field label="Given Name" value={v.given_name} />
              <Field label="Surname" value={v.surname} />
              <Field label="Sex" value={v.gender} />
              <Field label="Nationality" value={v.nationality} />
              <Field label="Date of birth" value={v.birth_date} />
              <Field label="Place of birth" value={v.place_of_birth} />
            </Section>

            <Section title="Passport — document">
              <Field label="Passport No." value={v.passport_number} />
              <Field label="Date of issue" value={v.passport_issue_date} />
              <div className="hidden md:block" aria-hidden="true" />
              <Field label="Date of expiry" value={v.passport_expiry_date} />
            </Section>
          </>
        )}

        {/* Actions */}
        {isDeclineActive ? (
          <div className="space-y-4 border-t border-gray-200 pt-4 mt-2">
            <div className="space-y-2">
              <label
                htmlFor="decline-reason"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for declining
              </label>
              <select
                id="decline-reason"
                value={selectedReason}
                onChange={handleReasonSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>
                  Select a reason
                </option>
                {DECLINE_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="text-reason"
                className="block text-sm font-medium text-gray-700"
              >
                {selectedReason === "Other"
                  ? "Custom reason"
                  : "Edit reason (if needed)"}
              </label>
              <textarea
                id="text-reason"
                placeholder={
                  selectedReason === "Other"
                    ? "Please specify the reason for declining"
                    : "Edit the reason if needed"
                }
                value={textReason}
                onChange={(e) => setTextReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleDecline}
                disabled={!selectedReason || !textReason.trim() || isLoading}
                className={`px-4 py-2 rounded-md text-white ${
                  !selectedReason || !textReason.trim() || isLoading
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isLoading ? "Processing…" : "Confirm Decline"}
              </button>
              <button
                onClick={() => setIsDeclineActive(false)}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center border-t border-gray-200 pt-4 mt-2">
            <button
              onClick={() => setIsDeclineActive(true)}
              disabled={isLoading || !v}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4 text-red-600" />
              Decline
            </button>
            <div className="flex items-center gap-2">
              {onSkip && (
                <button
                  onClick={handleSkip}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <SkipForward className="h-4 w-4 text-gray-600" />
                  Skip
                </button>
              )}
              <button
                onClick={handleAccept}
                disabled={isLoading || !v}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Accept
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
